import { useCallback, useMemo, useState } from 'react';
import type { ProjectStepId, PublishDevice } from './types';

export const PROJECT_ALL_STEPS: readonly ProjectStepId[] = [
  'select-scenes',
  'choose-platform',
  'project-info',
  'stories',
  'confirmation',
] as const;

export const PROJECT_STEP_LABELS: Record<ProjectStepId, string> = {
  'select-scenes': 'Select',
  'choose-platform': 'Platform',
  'project-info': 'Project',
  stories: 'Stories',
  confirmation: 'Confirm',
};

const DEFAULT_DEVICES: PublishDevice[] = ['app'];

export function getVisibleProjectSteps(devices: PublishDevice[]): ProjectStepId[] {
  const steps: ProjectStepId[] = ['select-scenes', 'choose-platform'];
  if (devices.includes('app')) steps.push('project-info');
  steps.push('stories', 'confirmation');
  return steps;
}

export interface UseProjectPublishFlowResult {
  devices: PublishDevice[];
  toggleDevice: (d: PublishDevice) => void;
  visibleSteps: ProjectStepId[];
  currentStepIndex: number;
  currentStep: ProjectStepId;
  goNext: () => void;
  goBack: () => void;
  goToStep: (id: ProjectStepId) => void;
  canGoBack: boolean;
  isLastStep: boolean;
  reset: () => void;
}

export function useProjectPublishFlow(): UseProjectPublishFlowResult {
  const [devices, setDevices] = useState<PublishDevice[]>(DEFAULT_DEVICES);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const visibleSteps = useMemo(() => getVisibleProjectSteps(devices), [devices]);
  const currentStep = visibleSteps[Math.min(currentStepIndex, visibleSteps.length - 1)];
  const isLastStep = currentStepIndex >= visibleSteps.length - 1;
  const canGoBack = currentStepIndex > 0;

  const toggleDevice = useCallback((d: PublishDevice) => {
    setDevices((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  }, []);

  const goNext = useCallback(() => setCurrentStepIndex((i) => i + 1), []);
  const goBack = useCallback(() => setCurrentStepIndex((i) => Math.max(0, i - 1)), []);
  const goToStep = useCallback(
    (id: ProjectStepId) => {
      const idx = visibleSteps.indexOf(id);
      if (idx !== -1) setCurrentStepIndex(idx);
    },
    [visibleSteps],
  );

  const reset = useCallback(() => {
    setDevices(DEFAULT_DEVICES);
    setCurrentStepIndex(0);
  }, []);

  return {
    devices,
    toggleDevice,
    visibleSteps,
    currentStepIndex,
    currentStep,
    goNext,
    goBack,
    goToStep,
    canGoBack,
    isLastStep,
    reset,
  };
}
